/**
 * Dashboard Page - Comprehensive View
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import {
  ShoppingCart as OrdersIcon,
  MedicalServices as ServicesIcon,
  People as UsersIcon,
  AttachMoney as RevenueIcon,
  VerifiedUser as VerifiedIcon,
  Star as StarIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { PageHeader, StatsCard, StatusChip } from '../../components/common';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { palette } from '../../theme/palette';
import { useGetKPIsQuery } from '../../store/api/analyticsApi';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { formatPercent, formatNumber, formatCurrency } from '../../utils/format';

// Custom Tooltip Components for Better Hover Experience
const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 3,
        }}
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="primary.main">
          Orders: {payload[0].value}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const CustomLineTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 3,
        }}
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="primary.main">
          Orders: {payload[0].value}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const CustomPieTooltip = ({ active, payload }: any) => {
  const { t } = useTranslation();

  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 3,
        }}
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {payload[0].name}
        </Typography>
        <Typography variant="body2" color="primary.main">
          {t('dashboard.count')}: {formatNumber(payload[0].value)}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const RADIAN = Math.PI / 180;

/**
 * Draw the share as a localized percentage inside the donut ring.
 * Names live in the legend, so keeping them off the slices avoids the
 * overlapping label lines that RTL text made unreadable.
 */
const renderPieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}: any) => {
  const share = (percent ?? 0) * 100;
  // Skip slivers — a label would not fit and would collide with its neighbours.
  if (!value || share < 5) return null;

  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={700}
    >
      {formatPercent(share, 0)}
    </text>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useAppSelector((state) => state.ui);
  const isRTL = language === 'ar';
  const { data: kpisData, isLoading, error } = useGetKPIsQuery();

  // Number & currency formatting is locale-aware via utils/format.

  // Format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Calculate trend (simple comparison - this month vs this week as proxy)
  const calculateTrend = (current: number, previous: number): { value: number; direction: 'up' | 'down' } => {
    if (previous === 0) {
      return current > 0 ? { value: 100, direction: 'up' } : { value: 0, direction: 'up' };
    }
    const change = ((current - previous) / previous) * 100;
    const value = Math.round(Math.abs(change) * 10) / 10; // 1 decimal, avoid float noise
    return {
      value,
      direction: change >= 0 ? 'up' : 'down',
    };
  };

  // Prepare order status data for pie chart
  const orderStatusData = useMemo(() => {
    if (!kpisData?.data?.orders) return [];

    const orders = kpisData.data.orders;
    const total = orders.total_orders;
    if (total === 0) return [];

    const data = [
      {
        name: t('dashboard.completed'),
        value: orders.completed_orders,
        color: palette.status.success,
      },
      {
        name: t('dashboard.inProgress'),
        value: orders.in_progress_orders,
        color: palette.brand[500],
      },
      {
        name: t('dashboard.confirmed'),
        value: orders.confirmed_orders,
        color: palette.brandSecondary[500],
      },
      {
        name: t('dashboard.pending'),
        value: orders.pending_orders,
        color: palette.status.warning,
      },
      {
        name: t('dashboard.cancelled'),
        value: orders.cancelled_orders,
        color: palette.status.danger,
      },
    ].filter((item) => item.value > 0);

    return data;
  }, [kpisData, t]);

  // Prepare popular services data for bar chart
  const popularServicesData = useMemo(() => {
    if (!kpisData?.data?.services?.popular_services) return [];
    return kpisData.data.services.popular_services
      .filter((service) => service.order_count > 0)
      .sort((a, b) => b.order_count - a.order_count)
      .slice(0, 5);
  }, [kpisData]);

  // Prepare payment methods data for pie chart
  const paymentMethodsData = useMemo(() => {
    if (!kpisData?.data?.revenue) return [];
    const revenue = kpisData.data.revenue;
    const data = [
      {
        name: t('dashboard.cashPayments'),
        value: revenue.cash_payments,
        color: palette.status.success,
      },
      {
        name: t('dashboard.onlinePayments'),
        value: revenue.online_payments,
        color: palette.brand[500],
      },
    ].filter((item) => item.value > 0);
    return data;
  }, [kpisData, t]);

  // Prepare orders by day data
  const ordersByDay = useMemo(() => {
    const days = isRTL
      ? ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const totalOrders = kpisData?.data?.orders?.orders_this_week || 0;
    const recentOrders = kpisData?.data?.orders?.recent_orders || [];

    // Calculate the start of the current week (Monday)
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to Monday
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() + mondayOffset);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);

    // Initialize day counts
    const dayCounts = new Array(7).fill(0);

    // Count orders per day of week from recent_orders
    recentOrders.forEach((order) => {
      try {
        const orderDate = new Date(order.created_at);
        // Check if order is within this week
        if (orderDate >= weekStart && orderDate < weekEnd) {
          const dayIndex = orderDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
          // Convert to Monday-based index (0 = Monday, 6 = Sunday)
          const mondayBasedIndex = dayIndex === 0 ? 6 : dayIndex - 1;
          dayCounts[mondayBasedIndex]++;
        }
      } catch (e) {
        // Skip invalid dates
      }
    });

    // If we have total orders but no day-specific data, distribute evenly
    const totalCounted = dayCounts.reduce((sum, count) => sum + count, 0);
    if (totalOrders > 0 && totalCounted === 0) {
      // Distribute orders evenly across days
      const ordersPerDay = Math.floor(totalOrders / 7);
      const remainder = totalOrders % 7;
      for (let i = 0; i < 7; i++) {
        dayCounts[i] = ordersPerDay + (i < remainder ? 1 : 0);
      }
    }

    return days.map((day, index) => ({
      name: day,
      orders: dayCounts[index] || 0,
    }));
  }, [kpisData, isRTL]);

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 2,
            mb: 3,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('dashboard.failedToLoad')}
        </Alert>
      </Box>
    );
  }

  const data = kpisData?.data;
  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('common.noData')}</Alert>
      </Box>
    );
  }

  // Calculate trends
  const ordersTrend = calculateTrend(
    data.orders.orders_this_month,
    data.orders.orders_this_week
  );
  const servicesTrend = calculateTrend(
    data.services.active_services,
    data.services.total_services - data.services.active_services
  );
  const usersTrend = calculateTrend(
    data.users.users_this_month,
    data.users.users_this_week
  );
  const revenueTrend = calculateTrend(
    data.revenue.revenue_this_month,
    data.revenue.revenue_this_week
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
      />

      {/* Main Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <StatsCard
          title={t('dashboard.totalOrders')}
          value={formatNumber(data.orders.total_orders)}
          icon={<OrdersIcon />}
          trend={ordersTrend}
          color="primary"
        />
        <StatsCard
          title={t('dashboard.activeServices')}
          value={formatNumber(data.services.active_services)}
          icon={<ServicesIcon />}
          trend={servicesTrend}
          color="secondary"
        />
        <StatsCard
          title={t('dashboard.totalUsers')}
          value={formatNumber(data.users.total_users)}
          icon={<UsersIcon />}
          trend={usersTrend}
          color="info"
        />
        <StatsCard
          title={t('dashboard.totalRevenue')}
          value={formatCurrency(data.revenue.total_revenue)}
          icon={<RevenueIcon />}
          trend={revenueTrend}
          color="success"
        />
      </Box>

      {/* Charts Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Orders Chart */}
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ textAlign: 'left' }}
            >
              {t('dashboard.ordersThisWeek')}
            </Typography>
            <Box sx={{ height: 300 }}>
              {ordersByDay.length > 0 && ordersByDay.some((d) => d.orders > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ordersByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" reversed={isRTL} />
                    <YAxis stroke="#64748B" orientation={isRTL ? 'right' : 'left'} />
                    <Tooltip content={<CustomLineTooltip />} cursor={{ stroke: palette.brand[500], strokeWidth: 1 }} />
                    <Line
                      type="monotone"
                      dataKey="orders"
                      stroke={palette.brand[500]}
                      strokeWidth={2}
                      dot={{ fill: palette.brand[500], r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography>{t('dashboard.noOrdersData')}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Order Status Pie Chart */}
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ textAlign: 'left' }}
            >
              {t('dashboard.orderStatusDistribution')}
            </Typography>
            <Box sx={{ height: 300 }}>
              {orderStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      labelLine={false}
                      label={renderPieLabel}
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography>{t('dashboard.noOrderStatusData')}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Detailed Stats Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Card>
          <CardContent>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ textAlign: 'left' }}
            >
              {t('dashboard.ordersToday')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber(data.orders.orders_today)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ textAlign: 'left' }}
            >
              {t('dashboard.activeUsers')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber(data.users.active_users)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ textAlign: 'left' }}
            >
              {t('dashboard.availableSpecialists')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber(data.specialists.available_specialists)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ textAlign: 'left' }}
            >
              {t('dashboard.pendingPayments')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(data.revenue.pending_amount)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Detailed Information Sections */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Popular Services */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row' }}>
              <ServicesIcon sx={{ mr: 1, color: palette.brand[500] }} />
              <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
                {t('dashboard.popularServices')}
              </Typography>
            </Box>
            {popularServicesData.length > 0 ? (
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={popularServicesData}
                    margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" reversed={isRTL} />
                    <YAxis stroke="#64748B" orientation={isRTL ? 'right' : 'left'} />
                    <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
                    <Bar dataKey="order_count" fill={palette.brand[500]} radius={[4, 4, 0, 0]} barSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">{t('dashboard.noPopularServicesData')}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row' }}>
              <PaymentIcon sx={{ mr: 1, color: palette.brand[500] }} />
              <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
                {t('dashboard.paymentMethods')}
              </Typography>
            </Box>
            <Box sx={{ height: 300 }}>
              {paymentMethodsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentMethodsData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      labelLine={false}
                      label={renderPieLabel}
                    >
                      {paymentMethodsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography>{t('dashboard.noPaymentData')}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Detailed Statistics Tables */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Users Statistics */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row' }}>
              <UsersIcon sx={{ mr: 1, color: palette.brand[500] }} />
              <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
                {t('dashboard.usersStatistics')}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.totalUsersLabel')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.users.total_users)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.activeUsersLabel')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.users.active_users)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.verifiedUsers')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isRTL ? 'flex-start' : 'flex-end', gap: 1, flexDirection: 'row' }}>
                        <Typography variant="body1" fontWeight={600}>
                          {formatNumber(data.users.verified_users)}
                        </Typography>
                        <VerifiedIcon fontSize="small" color="success" />
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.usersToday')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.users.users_today)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.usersThisWeek')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.users.users_this_week)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.usersThisMonth')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.users.users_this_month)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Services Statistics */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row' }}>
              <ServicesIcon sx={{ mr: 1, color: palette.brand[500] }} />
              <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
                {t('dashboard.servicesStatistics')}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.totalServices')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.services.total_services)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.activeServicesLabel')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.services.active_services)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.totalSubservices')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.services.total_subservices)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.activeSubservices')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.services.active_subservices)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Specialists & Orders Statistics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Specialists Statistics */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row' }}>
              <UsersIcon sx={{ mr: 1, color: palette.brand[500] }} />
              <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
                {t('dashboard.specialistsStatistics')}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.totalSpecialists')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.specialists.total_specialists)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.activeSpecialists')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.specialists.active_specialists)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.availableSpecialistsLabel')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.specialists.available_specialists)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.averageRating')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isRTL ? 'flex-start' : 'flex-end', gap: 0.5, flexDirection: 'row' }}>
                        <StarIcon fontSize="small" sx={{ color: palette.status.warning }} />
                        <Typography variant="body1" fontWeight={600}>
                          {data.specialists.average_rating.toFixed(1)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {data.specialists.top_rated_specialists && data.specialists.top_rated_specialists.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ textAlign: 'left' }}
                >
                  {t('dashboard.topRatedSpecialists')}
                </Typography>
                {data.specialists.top_rated_specialists.map((specialist) => (
                  <Box key={specialist.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, flexDirection: 'row' }}>
                    <Typography variant="body2" sx={{ textAlign: 'left' }}>{specialist.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: 'row' }}>
                      <StarIcon fontSize="small" sx={{ color: palette.status.warning }} />
                      <Typography variant="body2" fontWeight={600}>
                        {Number(specialist.rating ?? 0).toFixed(1)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Orders Statistics */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row' }}>
              <OrdersIcon sx={{ mr: 1, color: palette.brand[500] }} />
              <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
                {t('dashboard.ordersStatistics')}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.totalOrdersLabel')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.orders.total_orders)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.ordersTodayLabel')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.orders.orders_today)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.ordersThisWeekLabel')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.orders.orders_this_week)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.ordersThisMonth')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={600}>
                        {formatNumber(data.orders.orders_this_month)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.pending')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatNumber(data.orders.pending_orders)}
                        size="small"
                        color="warning"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        {t('dashboard.completed')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatNumber(data.orders.completed_orders)}
                        size="small"
                        color="success"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Revenue Statistics */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row' }}>
            <RevenueIcon sx={{ mr: 1, color: palette.brand[500] }} />
            <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
              {t('dashboard.revenueStatistics')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.totalRevenueLabel')}
              </Typography>
              <Typography variant="h6" fontWeight={700} color="success.main" sx={{ textAlign: 'left' }}>
                {formatCurrency(data.revenue.total_revenue)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.revenueToday')}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ textAlign: 'left' }}>
                {formatCurrency(data.revenue.revenue_today)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.revenueThisWeek')}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ textAlign: 'left' }}>
                {formatCurrency(data.revenue.revenue_this_week)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.revenueThisMonth')}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ textAlign: 'left' }}>
                {formatCurrency(data.revenue.revenue_this_month)}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.completedPayments')}
              </Typography>
              <Typography variant="body1" fontWeight={600} color="success.main" sx={{ textAlign: 'left' }}>
                {formatNumber(data.revenue.completed_payments)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.pendingPaymentsLabel')}
              </Typography>
              <Typography variant="body1" fontWeight={600} color="warning.main" sx={{ textAlign: 'left' }}>
                {formatNumber(data.revenue.pending_payments)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.failedPayments')}
              </Typography>
              <Typography variant="body1" fontWeight={600} color="error.main" sx={{ textAlign: 'left' }}>
                {formatNumber(data.revenue.failed_payments)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.cashPayments')}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ textAlign: 'left' }}>
                {formatNumber(data.revenue.cash_payments)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.onlinePayments')}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ textAlign: 'left' }}>
                {formatNumber(data.revenue.online_payments)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textAlign: 'left' }}>
                {t('dashboard.pendingAmount')}
              </Typography>
              <Typography variant="body1" fontWeight={600} color="warning.main" sx={{ textAlign: 'left' }}>
                {formatCurrency(data.revenue.pending_amount)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Recent Orders Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexDirection: 'row' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
              <OrdersIcon sx={{ mr: 1, color: palette.brand[500] }} />
              <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'left' }}>
                {t('dashboard.recentOrders')}
              </Typography>
            </Box>
            {data.orders.recent_orders.length > 0 && (
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => navigate('/orders')}
              >
                {t('dashboard.viewAll')}
              </Typography>
            )}
          </Box>
          {data.orders.recent_orders && data.orders.recent_orders.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: 'left' }}>{t('dashboard.orderId')}</TableCell>
                    <TableCell sx={{ textAlign: 'left' }}>{t('dashboard.customer')}</TableCell>
                    <TableCell sx={{ textAlign: 'left' }}>{t('dashboard.service')}</TableCell>
                    <TableCell sx={{ textAlign: 'left' }}>{t('dashboard.amount')}</TableCell>
                    <TableCell sx={{ textAlign: 'left' }}>{t('dashboard.status')}</TableCell>
                    <TableCell sx={{ textAlign: 'left' }}>{t('dashboard.createdAt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.orders.recent_orders.map((order) => {
                    const userName = `${order.user__first_name || ''} ${order.user__last_name || ''}`.trim() || 'N/A';

                    return (
                      <TableRow
                        key={order.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/orders/${order.order_number || order.id}`)}
                      >
                        <TableCell sx={{ textAlign: 'left' }}>
                          <Typography variant="body2" fontWeight={600}>
                            #{order.order_number || order.id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left' }}>
                          <Typography variant="body2">{userName}</Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left' }}>
                          <Typography variant="body2">
                            {order.subservice__name?.trim() ? order.subservice__name : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left' }}>
                          <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'left' }}>
                            {formatCurrency(parseFloat(order.total_amount))}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left' }}>
                          <StatusChip status={order.status as any} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left' }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(order.created_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('dashboard.noRecentOrders')}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;
